import Head from 'next/head';
import PropTypes from 'prop-types';
import Script from 'next/script';

const Metadata = ({ metadata, isRobot }) => {
  if (!metadata) return;

  const vanillaScripts = metadata.headSectionScripts
    .map((s) => {
      if (!s || s === '') return null;
      return s.replace(/<script[^>]*>/g, '').replace(/<\/script>/g, '');
    })
    .join('\n');

  const scriptSrcs = [];
  metadata.headSectionScripts.forEach((s) => {
    if (s && s !== '') {
      const sm = /src\s*=\s*"([^"]+)/g.exec(s);
      if (sm) {
        scriptSrcs.push(...sm.filter((res) => !res.startsWith('src=')));
      }
    }
  });

  const hreflangs = [];

  if (metadata.languages && metadata.languages.length > 0) {
    let currentHost = null;
    if (process.env.NODE_ENV === 'development') {
      currentHost =
        (process.env.DEV_FRONTEND_HOST !== '' && process.env.DEV_FRONTEND_HOST) || 'https://qa.harbourhotels.co.uk/';
    } else {
      if (typeof window === 'undefined') {
        currentHost = process.env.RESOURCE_PATH.slice(0, -1);
      } else {
        currentHost = window?.location?.origin;
      }
    }

    metadata.languages.forEach((language) => {
      if (language.language === 'en-us') {
        hreflangs.push(
          <link
            key="alternate-x-default"
            rel="alternate"
            href={currentHost + language.languageUrl}
            hrefLang="x-default"
          />,
        );
      }

      const hrefLang = language.language === 'en-us' ? 'en' : language.language;

      hreflangs.push(
        <link
          key={`alternate-${hrefLang}`}
          rel="alternate"
          href={currentHost + language.languageUrl}
          hrefLang={hrefLang}
        />,
      );
    });
  }

  return (
    <>
      {!isRobot && scriptSrcs.map((script, index) => <Script key={index} src={script} defer />)}

      <Head>
        <title>{metadata.pageTitle}</title>
        <meta name="keywords" content={metadata.metaKeywords} />
        <meta name="description" content={metadata.metaDescription} />
        {/* `property` is not one of next/head's dedupe attributes (it only checks name/httpEquiv/charSet/itemProp),
            so og tags need an explicit key or they get appended again on every client-side head update. */}
        <meta key="og:url" property="og:url" content={metadata.absolutePageUrl} />
        <meta key="og:type" property="og:type" content="website" />
        <meta key="og:title" property="og:title" content={metadata.openGraphPageTitle} />
        <meta key="og:description" property="og:description" content={metadata.openGraphDescription} />
        {/* Only render when we actually have a URL: SSR drops a nullish `content` attribute, but the client
            head manager only skips `undefined`, so a null here becomes content="null" and never matches. */}
        {metadata.openGraphImageUrl && <meta key="og:image" property="og:image" content={metadata.openGraphImageUrl} />}
        <meta name="google-site-verification" content="r0v_N5SIjkAtq5tE_tQibEh8GRUCzCa9sxyYYWSzUcM" />
        <link key="canonical" rel="canonical" href={metadata.canonicalLink} />

        {...hreflangs}

        {metadata.hideFromSearch && <meta name="robots" content="noindex" />}

        {/* {metadata.campaignStylesheet && (
          <link
            rel="stylesheet"
            href={`${process.env.CMS_URL}${metadata.campaignStylesheet.startsWith('/') ? metadata.campaignStylesheet.substring(1, metadata.campaignStylesheet.length) : metadata.campaignStylesheet}`}
          />
        )} */}

        {!isRobot && metadata.headSectionScripts && metadata.headSectionScripts.length > 0 && (
          <script
            dangerouslySetInnerHTML={{
              __html: vanillaScripts,
            }}
          />
        )}

        {metadata.structuredDataTags.map((element, index) => (
          <script
            key={index}
            type="application/ld+json"
            id="structuredDataTags"
            dangerouslySetInnerHTML={{ __html: element }}
          />
        ))}

        {metadata.favicon?.imageUrl && (
          <link key="favicon" rel="icon" type="image/png" href={metadata.favicon.imageUrl} />
        )}
      </Head>
    </>
  );
};

Metadata.propTypes = {
  metadata: PropTypes.object.isRequired,
};

export default Metadata;
